import { useEffect, useRef, useState } from 'react';
import { ProjectType } from '@bulthaup/api/db';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Image,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { MIME_TYPES } from '@mantine/dropzone';
import { FormValidateInput, useForm } from '@mantine/form';
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconEraser,
  IconPencil,
  IconPhoto,
} from '@tabler/icons-react';
import { merge } from 'lodash';
import { zodResolver } from 'mantine-form-zod-resolver';
import SignatureCanvas from 'react-signature-canvas';
import { z } from 'zod';

import { DeletableImage } from '../-components/technical-plan/-deletable-image';
import { FileUploadButton } from '../-components/technical-plan/-file-upload-button';
import { RouterOutput } from '../../../../../services/trpc';
import {
  getUTDocumentUrl,
  useUploadThing,
} from '../../../../../services/upload';
import { handleFormError } from '../../../../../utils/handle-form-error';
import { useSignatureCanvas } from './-use-signature-canvas';

export interface ConstructionSiteReportFormValues {
  date?: string;
  attendees: string[];
  extraAttendee: string;
  question1?: boolean;
  question2?: boolean;
  question3?: boolean;
  question4?: boolean;
  question5?: boolean;
  question6?: boolean;
  question3Extra: 'PANEL_NOT_CONNECTED' | 'MISC' | null;
  question3ExtraObservations: string;
  observations: string;
  signer: string;
  signatureUtFileKey: string;
  imagesUtFileKeys: string[];
}

interface YesNoQuestionProps {
  label: string;
  value: boolean | undefined;
  onChange: (value: boolean) => void;
}

function YesNoQuestion({ label, value, onChange }: YesNoQuestionProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        width: '100%',
        gap: 16,
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
      <div
        style={{
          display: 'flex',
          gap: 16,
          minWidth: 80,
        }}
      >
        <Checkbox
          styles={{ label: { paddingLeft: 4 } }}
          label="Oui"
          checked={value === true}
          onChange={() => onChange(true)}
        />
        <Checkbox
          styles={{ label: { paddingLeft: 4 } }}
          label="Non"
          checked={value === false}
          onChange={() => onChange(false)}
        />
      </div>
    </div>
  );
}

export function ConstructionSiteReportForm({
  project,
  onSubmit,
  initialValues,
}: {
  project: RouterOutput['projects']['get'];
  onSubmit: (values: ConstructionSiteReportFormValues) => void;
  initialValues?: ConstructionSiteReportFormValues;
}) {
  const { startUpload } = useUploadThing('signatureUploader');
  const [step, setStep] = useState<'form' | 'photos' | 'signature'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm<ConstructionSiteReportFormValues>({
    initialValues: merge(
      {
        date: undefined,
        attendees: [] as string[],
        extraAttendee: '',
        question1: undefined,
        question2: undefined,
        question3: undefined,
        question4: undefined,
        question5: undefined,
        question6: undefined,
        question3Extra: null,
        question3ExtraObservations: '',
        observations: '',
        signer: '',
        signatureUtFileKey: '',
        imagesUtFileKeys: [],
      },
      initialValues || {},
    ),
    validate: zodResolver(
      z.object({
        date: z.string(),
        attendees: z.array(z.string()),
        question1: z.boolean(),
        question2: z.boolean(),
        question3: z.boolean(),
        question4: z.boolean(),
        question5: z.boolean(),
        question6: z.boolean(),
        question3Extra: z.union([
          z.enum(['PANEL_NOT_CONNECTED', 'MISC']),
          z
            .null()
            .refine(
              (): boolean => form.values.question3 !== false,
              'Required when question3 is false',
            ),
        ]),
        question3ExtraObservations: z.string(),
        observations: z.string(),
        signer: z.string(),
        signatureUtFileKey: z.string(),
      }),
    ) as FormValidateInput<ConstructionSiteReportFormValues>,
  });
  const signatureRef = useRef<SignatureCanvas>(null);
  const { isSignatureDisabled, handleSignatureDraw, handleSignatureClear } =
    useSignatureCanvas();

  const handleSubmit = async (values: ConstructionSiteReportFormValues) => {
    try {
      setIsSubmitting(true);
      if (step === 'signature') {
        if (!values.signer) {
          window.alert('Veuillez sélectionner un signataire');
          return;
        }
        const signature = signatureRef.current?.toDataURL();
        if (!signature) {
          throw new Error('Signature is required');
        }
        const response = await fetch(signature);
        const blob = await response.blob();
        const file = new File([blob], 'signature.png', { type: 'image/png' });

        const uploadResult = await startUpload([file]);
        if (uploadResult && uploadResult.length > 0) {
          values.signatureUtFileKey = uploadResult[0].key;
        } else {
          console.error('Failed to upload signature');
        }
      } else {
        values.signatureUtFileKey = '';
        values.signer = '';
      }
      await onSubmit(values);
    } catch (error) {
      handleFormError(form, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const client1Name = `${project.customer.customer1Title} ${project.customer.customer1FirstName} ${project.customer.customer1LastName}`;
  const client2Name = `${project.customer.customer2Title} ${project.customer.customer2FirstName} ${project.customer.customer2LastName}`;

  const toggleAttendee = (name: string) => {
    form.setFieldValue(
      'attendees',
      form.values.attendees.includes(name)
        ? form.values.attendees.filter(a => a !== name)
        : [...form.values.attendees, name],
    );
  };

  useEffect(() => {
    if (step === 'signature') {
      signatureRef.current?.clear();
      handleSignatureClear();
    }
  }, [step]);

  const nbRequiredImages = project.type === ProjectType.KITCHEN ? 5 : 3;
  const hasRequiredNbOfImages =
    form.values.imagesUtFileKeys.length >= nbRequiredImages;

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      {step === 'form' && (
        <Stack>
          <DateInput
            withWeekNumbers
            lang="fr"
            label="Date de la visite"
            required
            valueFormat="LL"
            {...form.getInputProps('date')}
          />

          <Stack gap={4}>
            <p>Personnes présentes:</p>
            <Checkbox
              label={client1Name}
              {...form.getInputProps('attendees', { type: 'checkbox' })}
              checked={form.values.attendees.includes(client1Name)}
              onChange={() => toggleAttendee(client1Name)}
            />
            {project.customer.customer2FirstName && (
              <Checkbox
                label={client2Name}
                {...form.getInputProps('attendees', { type: 'checkbox' })}
                checked={form.values.attendees.includes(client2Name)}
                onChange={() => toggleAttendee(client2Name)}
              />
            )}
            {project.customer.contacts.map(contact => {
              const name = `${contact.firstName} ${contact.lastName}`;
              return (
                <Checkbox
                  key={contact.id}
                  label={name}
                  {...form.getInputProps('attendees', { type: 'checkbox' })}
                  checked={form.values.attendees.includes(name)}
                  onChange={() => toggleAttendee(name)}
                />
              );
            })}

            <Group>
              <Text>Autre personne présente :</Text>
              <TextInput
                placeholder="Nom"
                value={form.values.extraAttendee}
                onChange={event =>
                  form.setFieldValue('extraAttendee', event.currentTarget.value)
                }
              />
            </Group>
          </Stack>

          <YesNoQuestion
            label="Les travaux exécutés sont conformes aux plans et descriptifs du marché"
            value={form.values.question1}
            onChange={value => form.setFieldValue('question1', value)}
          />

          <YesNoQuestion
            label="Les plans de travail sont en bon état"
            value={form.values.question2}
            onChange={value => form.setFieldValue('question2', value)}
          />

          <YesNoQuestion
            label="Les appareils ménagers ont pu être testés"
            value={form.values.question3}
            onChange={value => {
              form.setFieldValue('question3', value);
              if (value === true) {
                form.setFieldValue('question3Extra', null);
                form.setFieldValue('question3ExtraObservations', '');
              }
            }}
          />

          {form.values.question3 === false && (
            <>
              <Stack gap={4}>
                <Checkbox
                  label="Panneaux non connectés"
                  checked={form.values.question3Extra === 'PANEL_NOT_CONNECTED'}
                  onChange={event => {
                    if (event.currentTarget.checked) {
                      form.setFieldValue(
                        'question3Extra',
                        'PANEL_NOT_CONNECTED',
                      );
                      form.setFieldValue('question3ExtraObservations', '');
                    }
                  }}
                />
                <Checkbox
                  label="Divers"
                  checked={form.values.question3Extra === 'MISC'}
                  onChange={event => {
                    if (event.currentTarget.checked) {
                      form.setFieldValue('question3Extra', 'MISC');
                    }
                  }}
                />
              </Stack>

              {form.values.question3Extra === 'MISC' && (
                <TextInput
                  label="Observations"
                  {...form.getInputProps('question3ExtraObservations')}
                />
              )}
            </>
          )}

          <YesNoQuestion
            label="Les façades sont en bon état"
            value={form.values.question4}
            onChange={value => form.setFieldValue('question4', value)}
          />

          <YesNoQuestion
            label="La cuisine a été nettoyée"
            value={form.values.question5}
            onChange={value => form.setFieldValue('question5', value)}
          />

          <YesNoQuestion
            label="Demande de modification par le client"
            value={form.values.question6}
            onChange={value => form.setFieldValue('question6', value)}
          />

          <Textarea
            label="Observations générales"
            minRows={4}
            {...form.getInputProps('observations')}
          />

          <Group justify="flex-end">
            <Button
              leftSection={<IconArrowRight size={16} />}
              loading={isSubmitting}
              disabled={!form.isValid()}
              onClick={() => {
                setStep('photos');
              }}
            >
              Continuer
            </Button>
          </Group>
        </Stack>
      )}
      {step === 'photos' && (
        <Stack gap="xl">
          <Title order={4}>Photos du chantier</Title>
          <Stack gap="md">
            <Stack align="center">
              <FileUploadButton
                buttonProps={{
                  variant: hasRequiredNbOfImages ? 'outline' : 'filled',
                  size: 'lg',
                  leftSection: <IconPhoto size={16} />,
                }}
                // leftSection={<IconPhoto size={16} />}
                placeholder="Ajouter des photos"
                accept={`${MIME_TYPES.png},${MIME_TYPES.jpeg},${MIME_TYPES.webp}`}
                utUploadKey="constructionSiteReportImagesUploader"
                multiple
                onChange={files =>
                  form.setFieldValue('imagesUtFileKeys', [
                    ...form.values.imagesUtFileKeys,
                    ...files.map(f => f.key),
                  ])
                }
              />
              <Text c="red">
                Veuillez ajouter au moins {nbRequiredImages} photos pour
                valider.
              </Text>
            </Stack>
            {form.values.imagesUtFileKeys.length > 0 && (
              <Group gap="sm">
                {form.values.imagesUtFileKeys.map(key => (
                  <DeletableImage
                    onDelete={() => {
                      form.setFieldValue(
                        'imagesUtFileKeys',
                        form.values.imagesUtFileKeys.filter(k => k !== key),
                      );
                    }}
                  >
                    <Image
                      src={getUTDocumentUrl(key)}
                      height={70}
                      width="auto"
                      fit="contain"
                      style={{ maxHeight: '100px' }}
                    />
                  </DeletableImage>
                ))}
              </Group>
            )}
          </Stack>

          <Divider />

          <Group justify="space-between">
            <Button
              leftSection={<IconArrowLeft size={16} />}
              variant="outline"
              disabled={isSubmitting}
              onClick={() => setStep('form')}
            >
              Retour
            </Button>
            <Group justify="flex-end" gap="xs">
              {[...form.values.attendees, form.values.extraAttendee].filter(
                Boolean,
              ).length > 0 ? (
                <Button
                  leftSection={<IconPencil size={16} />}
                  loading={isSubmitting}
                  disabled={!hasRequiredNbOfImages}
                  onClick={() => {
                    setStep('signature');
                  }}
                >
                  Faire signer sur place
                </Button>
              ) : (
                <Button
                  leftSection={<IconCheck size={16} />}
                  type="submit"
                  loading={isSubmitting}
                  disabled={!hasRequiredNbOfImages}
                >
                  Valider
                </Button>
              )}
            </Group>
          </Group>
        </Stack>
      )}
      {step === 'signature' && (
        <Stack style={{ maxWidth: 500, margin: '0 auto' }}>
          <Select
            allowDeselect={false}
            required
            label="Signataire"
            placeholder="Nom du signataire"
            value={form.values.signer}
            data={[...form.values.attendees, form.values.extraAttendee]
              .filter(Boolean)
              .map(attendee => ({
                label: attendee,
                value: attendee,
              }))}
            onChange={value => {
              form.setFieldValue('signer', value || '');
            }}
          />

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Box>Signature</Box>
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{
                height: 600,
                style: {
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  pointerEvents: isSignatureDisabled ? 'none' : 'auto',
                  backgroundColor: isSignatureDisabled
                    ? 'rgba(0, 0, 0, 0.1)'
                    : 'white',
                },
              }}
              onBegin={handleSignatureDraw}
              onEnd={handleSignatureDraw}
            />
          </Box>
          <Group justify="flex-end">
            <Button
              leftSection={<IconEraser size={16} />}
              variant="outline"
              onClick={() => {
                signatureRef.current?.clear();
                handleSignatureClear();
              }}
            >
              Effacer
            </Button>
          </Group>
          <Divider />
          <Group justify="space-between">
            <Button
              leftSection={<IconArrowLeft size={16} />}
              variant="outline"
              disabled={isSubmitting}
              onClick={() => setStep('photos')}
            >
              Retour
            </Button>
            <Button
              loading={isSubmitting}
              type="submit"
              leftSection={<IconCheck size={16} />}
            >
              Valider
            </Button>
          </Group>
        </Stack>
      )}
    </form>
  );
}
